import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import {
  buildRequestFailureCacheKey,
  clearRequestFailure,
} from '../../shared/api/requestFailureCache.js';
import { showToast } from '../../shared/ui/toastStore.js';
import type { ReceiptLocalePreferenceDTO } from './accountTypes.js';
import {
  resolveReceiptUiLocale,
  type ReceiptLocaleCode,
} from './receiptLocalePreference.js';
import {
  isDefaultReceiptPreference,
  needsReceiptUiLocaleSync,
  nextCoalescedLocale,
} from './receiptLocaleSync.js';
import {
  registerReceiptLocaleSyncRetry,
  setReceiptLocaleSyncStatus,
} from './receiptLocaleSyncStatus.js';
import { isAccountQueryEnabled } from './accountApi.js';

export { getLastReceiptLocaleSyncError } from './receiptLocaleSyncStatus.js';

/** Quiet remount / login race recovery — avoid alarming toast for background sync. */
export const MOUNT_SOFT_RETRY_MS = 2_000;

const RECEIPT_LOCALES_PATH = '/customer-account/preferences/receipt-locales';

export function clearLastReceiptLocaleSyncError(): void {
  setReceiptLocaleSyncStatus('idle');
}

function clearReceiptLocalesGetFailure(tenantCode: string): void {
  clearRequestFailure(
    buildRequestFailureCacheKey(['GET', tenantCode, RECEIPT_LOCALES_PATH]),
  );
}

async function requestWithOneRetry<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch {
    return await run();
  }
}

/**
 * While receipt preference is Default (follow UI), keep server `uiLocale` aligned
 * with i18n so receipt generation matches the live UI language without Account → Save.
 */
export function useSyncReceiptLocaleOnLanguageChange(): void {
  const { t, i18n } = useTranslation('customer');
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const customerId = session?.customerId;
  /** G5 — tenant ApiClient sync only when access usable + home jwt (not customerId alone). */
  const syncEnabled = isAccountQueryEnabled(session, tenantCode);
  const syncingRef = useRef(false);
  const pendingLocaleRef = useRef<ReceiptLocaleCode | null>(null);
  const toastShownForErrorRef = useRef(false);
  /** Only user language switches / Account Retry should surface a danger toast. */
  const toastOnFailureRef = useRef(false);
  const mountSoftRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountSoftRetryUsedRef = useRef(false);

  useEffect(() => {
    if (!syncEnabled || customerId === undefined) {
      registerReceiptLocaleSyncRetry(null);
      return;
    }

    mountSoftRetryUsedRef.current = false;

    const clearMountSoftRetry = (): void => {
      if (mountSoftRetryTimerRef.current !== null) {
        clearTimeout(mountSoftRetryTimerRef.current);
        mountSoftRetryTimerRef.current = null;
      }
    };

    const scheduleQuietMountRetry = (): void => {
      if (
        toastOnFailureRef.current ||
        mountSoftRetryUsedRef.current ||
        mountSoftRetryTimerRef.current !== null
      ) {
        return;
      }
      mountSoftRetryUsedRef.current = true;
      mountSoftRetryTimerRef.current = setTimeout(() => {
        mountSoftRetryTimerRef.current = null;
        toastOnFailureRef.current = false;
        // Soft auth 'none' / network may have marked this GET — clear before re-hit.
        clearReceiptLocalesGetFailure(tenantCode);
        enqueueLocale(resolveReceiptUiLocale(i18n.resolvedLanguage ?? i18n.language));
      }, MOUNT_SOFT_RETRY_MS);
    };

    const runSync = async (uiLocale: ReceiptLocaleCode): Promise<void> => {
      setReceiptLocaleSyncStatus('syncing');
      try {
        const cached = queryClient.getQueryData<ReceiptLocalePreferenceDTO>(
          customerQueryKeys.accountReceiptLocales(tenantCode, customerId)
        );
        let pref = cached;
        if (pref === undefined) {
          pref = await requestWithOneRetry(() =>
            api.request<ReceiptLocalePreferenceDTO>(RECEIPT_LOCALES_PATH)
          );
          queryClient.setQueryData(
            customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
            pref
          );
        }
        if (!isDefaultReceiptPreference(pref)) {
          setReceiptLocaleSyncStatus('idle');
          toastShownForErrorRef.current = false;
          return;
        }
        if (!needsReceiptUiLocaleSync(pref?.uiLocale, uiLocale)) {
          setReceiptLocaleSyncStatus('idle');
          toastShownForErrorRef.current = false;
          return;
        }
        const updated = await requestWithOneRetry(() =>
          api.request<ReceiptLocalePreferenceDTO>(RECEIPT_LOCALES_PATH, {
            method: 'PUT',
            body: { mode: 'default', uiLocale },
          })
        );
        queryClient.setQueryData(
          customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
          updated
        );
        setReceiptLocaleSyncStatus('idle');
        toastShownForErrorRef.current = false;
      } catch (err) {
        setReceiptLocaleSyncStatus('error', err);
        if (toastOnFailureRef.current && !toastShownForErrorRef.current) {
          toastShownForErrorRef.current = true;
          showToast({
            message: t('account.settings.receiptLocaleSyncFailed'),
            variant: 'danger',
            durationMs: 6000,
          });
        }
        // Background mount sync: one delayed silent retry (login race / transient 401).
        scheduleQuietMountRetry();
      }
    };

    const enqueueLocale = (uiLocale: ReceiptLocaleCode): void => {
      pendingLocaleRef.current = nextCoalescedLocale(
        pendingLocaleRef.current,
        uiLocale
      );
      if (syncingRef.current) {
        return;
      }
      syncingRef.current = true;
      void (async () => {
        try {
          while (pendingLocaleRef.current !== null) {
            const current = pendingLocaleRef.current;
            pendingLocaleRef.current = null;
            await runSync(current);
          }
        } finally {
          syncingRef.current = false;
          if (pendingLocaleRef.current !== null) {
            enqueueLocale(pendingLocaleRef.current);
          }
        }
      })();
    };

    const syncIfDefault = (rawLanguage: string): void => {
      enqueueLocale(resolveReceiptUiLocale(rawLanguage));
    };

    registerReceiptLocaleSyncRetry(() => {
      clearMountSoftRetry();
      toastShownForErrorRef.current = false;
      toastOnFailureRef.current = true;
      // Account Retry must not lose to a soft-auth-poisoned GET TTL.
      clearReceiptLocalesGetFailure(tenantCode);
      syncIfDefault(i18n.resolvedLanguage ?? i18n.language);
    });

    toastOnFailureRef.current = false;
    syncIfDefault(i18n.resolvedLanguage ?? i18n.language);

    const onLanguageChanged = (lng: string): void => {
      clearMountSoftRetry();
      toastShownForErrorRef.current = false;
      toastOnFailureRef.current = true;
      clearReceiptLocalesGetFailure(tenantCode);
      syncIfDefault(lng);
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      clearMountSoftRetry();
      i18n.off('languageChanged', onLanguageChanged);
      registerReceiptLocaleSyncRetry(null);
    };
  }, [api, customerId, syncEnabled, i18n, queryClient, t, tenantCode]);
}
